/**
 * Money formatting and parsing, shared by every surface that shows a price.
 *
 * Money is held as integer cents everywhere in the app, so nothing here ever
 * does arithmetic on a floating-point dollar amount — these functions only
 * convert between cents and the strings a user reads or types.
 *
 * Lifted out of `features/jobs/lib/jobForm` when the invoice builder needed the
 * same conversions: a price is not a job concept, and a second copy of
 * "$1,234.56" would have been the third place currency was defined.
 */

/** Parses a dollars-and-cents string into integer cents (0 when empty/invalid). */
export function parsePriceDollars(input: string): number {
  const cleaned = input.trim().replace(/[^0-9.]/g, "");
  if (!cleaned) return 0;
  const value = Math.round(Number(cleaned) * 100);
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

/** Formats integer cents as a currency string, e.g. 123456 -> "$1,234.56". */
export function formatPriceCents(cents: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format((cents ?? 0) / 100);
}

/** Renders cents back into a plain dollars input string (no symbol), e.g. "1234.56". */
export function centsToInputValue(cents: number): string {
  if (!cents) return "";
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}
